import { createSlice } from '@reduxjs/toolkit';

import {
  FeedItem,
  FeedItemType,
  FeedState,
  HideNotificationAction,
  HidePostsFromUIAction,
  LoadFeedAction,
  LoadMoreAction,
  SetActiveSectionAction,
  SetCommonFeedAction,
  SetFeedAction,
  SetFeedUsersSuggestionsAction,
  SetMoreFeedAction,
  SetPromotedBattleAction,
  SetScrollOffsetAction,
  UnshiftFeedAction,
} from './interface';
import { RootState } from 'store/rootReducer';

const initialState: FeedState = {
  feed: {},
  suggestions: [],
  notifications: [],
  hasNewFollowingPosts: false,
  hasNewSponsorsPosts: false,
  common: [],
  commonLoadingStatus: 'loading',
  commonNextFrom: '',
  commonLoadingMoreStatus: 'loaded',
  welcomeHidden: true,
  activeSection: null,
  promotedBattle: null,
  usersSuggestions: [],
};

export const { actions, reducer } = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    loadFeed(state, action: LoadFeedAction) {
      const { section } = action.payload;

      state.feed[section] = {
        ...state.feed[section],
        isLoading: true,
        isLoadingMore: true,
        nextFrom: '',
      };

      state.suggestions = [];
    },
    setFeed(state, action: SetFeedAction) {
      const {
        items,
        nextFrom,
        suggestions,
        notifications,
        hasNewFollowingPosts,
        hasNewSponsorsPosts,
        sources,
        welcomeHidden,
        section,
      } = action.payload;

      state.feed[section] = {
        ...state.feed[section],
        items,
        nextFrom,
        isLoadingMore: false,
        isLoading: false,
        sources,
        page: 1,
      };

      state.suggestions = suggestions;
      state.notifications = notifications;
      state.hasNewFollowingPosts = hasNewFollowingPosts;
      state.hasNewSponsorsPosts = hasNewSponsorsPosts;
      state.welcomeHidden = welcomeHidden;
    },

    loadMore(state, action: LoadMoreAction) {
      const { section } = action.payload;

      state.feed[section].isLoadingMore = true;
    },
    unshiftFeed(state, action: UnshiftFeedAction) {
      const { section, item } = action.payload;

      state.feed[section].items = [item, ...state.feed[section].items];
    },
    setMoreFeed(state, action: SetMoreFeedAction) {
      const {
        items,
        nextFrom,
        hasNewFollowingPosts,
        sources,
        hasNewSponsorsPosts,
        section,
      } = action.payload;
      const feedSection = state.feed[section];

      const old = feedSection?.items || [];
      const exist = {};
      for (let item of old) {
        if (
          item.type !== FeedItemType.MODEL &&
          item.type !== FeedItemType.ADS &&
          item.type !== FeedItemType.BUNDLE &&
          item.type !== FeedItemType.FEATURED_PROFILE &&
          item.type !== FeedItemType.AD_PROMO_PROFILE &&
          item.type !== FeedItemType.AD_PROMO_POST
        ) {
          exist[`${item.type}_${item.object}`] = true;
        }
      }

      feedSection.items = [...old].concat(
        items.filter((item) => !exist[`${item.type}_${item.object}`]),
      );

      state.feed[section] = {
        ...feedSection,
        nextFrom,
        isLoadingMore: false,
        sources: { ...feedSection.sources, ...sources },
        page: feedSection.page + 1,
      };

      state.hasNewFollowingPosts = hasNewFollowingPosts;
      state.hasNewSponsorsPosts = hasNewSponsorsPosts;
    },
    hideNotification(_, action: HideNotificationAction) {},
    hideNotificationFromUI(state, action: HideNotificationAction) {
      const notifications = [...state.notifications];
      for (let i = 0; i < notifications.length; i++) {
        if (notifications[i].id === action.payload) {
          notifications.splice(i, 1);
          break;
        }
      }

      state.notifications = notifications;
    },
    hidePostsFromUI(state, action: HidePostsFromUIAction) {
      const postsIdsData = action.payload;

      const section = state.activeSection;

      if (!section) return;

      const toHide = {};

      if (Array.isArray(postsIdsData)) {
        for (let postId of postsIdsData) {
          toHide[postId] = true;
        }
      } else {
        toHide[postsIdsData] = true;
      }

      const posts: FeedItem[] = [];
      const old = [...state.feed[section].items];
      for (let item of old) {
        const postId = +item.object;
        if (toHide[postId]) {
          continue;
        }

        posts.push(item);
      }

      state.feed[section].items = posts;
    },
    loadCommonFeed(state) {
      state.commonLoadingStatus = 'loading';
    },
    setCommonFeed(state, action: SetCommonFeedAction) {
      state.common = action.payload.items;
      state.commonNextFrom = action.payload.nextFrom;
      state.commonLoadingStatus = 'loaded';
    },
    commonFeedLoadingFailed(state) {
      state.commonLoadingStatus = 'failed';
    },
    commonFeedLoadMore(state) {
      state.commonLoadingStatus = 'loading';
    },
    setCommonFeedMore(state, action: SetCommonFeedAction) {
      const old = state.common;
      const exist = {};
      for (let item of old) {
        exist[`${item.type}_${item.object}`] = true;
      }

      state.common = [...old].concat(
        action.payload.items.filter((item) => !exist[`${item.type}_${item.object}`]),
      );
      state.commonNextFrom = action.payload.nextFrom;
      state.commonLoadingStatus = 'loaded';
    },
    commonFeedLoadMoreFailed(state) {
      state.commonLoadingStatus = 'loaded';
    },
    hideWelcome() {},
    setWelcomeHidden(state) {
      state.welcomeHidden = true;
    },
    clearFeed(state) {
      state.feed = {};
      state.activeSection = '';
    },
    clearSearch(state) {
      if (!state.feed['search']) {
        return;
      }

      const feedDuplicate = Object.assign({}, state.feed);
      delete feedDuplicate['search'];
      state.feed = feedDuplicate;
    },
    setActiveSection(state, action: SetActiveSectionAction) {
      state.activeSection = action.payload;
    },
    setScrollOffset(state, action: SetScrollOffsetAction) {
      const { section, scrollOffset } = action.payload;

      state.feed[section] = {
        ...state.feed[section],
        scrollOffset,
      };
    },
    setPromotedBattle(state, action: SetPromotedBattleAction) {
      state.promotedBattle = action.payload;
    },
    setUsersSuggestions(state, action: SetFeedUsersSuggestionsAction) {
      state.usersSuggestions = action.payload;
    },
  },
});

export { reducer as feedReducer, actions as feedActions };

export const feedSelector = (state: RootState) => state.feed;

export const usersSuggestionsSelector = (state: RootState) => state.feed.usersSuggestions;
